import React, { useEffect, useMemo, useState } from "react";
import {
  Box,
  Breadcrumbs,
  CardContent,
  Chip,
  Drawer,
  IconButton,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  Paper,
  Typography,
  useMediaQuery,
} from "@mui/material";
import MenuIcon from "@mui/icons-material/Menu";
import FolderIcon from "@mui/icons-material/Folder";
import DescriptionIcon from "@mui/icons-material/Description";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import ArrowBackIosNewIcon from "@mui/icons-material/ArrowBackIosNew";
import ArrowForwardIosIcon from "@mui/icons-material/ArrowForwardIos";
import { AllSideTabs } from "../../constants";
import { Side } from "../../types/DepthChartTypes";

type PlaybookNode = {
  id: string;
  label: string;
  children?: PlaybookNode[];
  plays?: string[];
  schemes?: Scheme[];
};

type Scheme = {
  id: string;
  label: string;
  concept: string;
  drawings?: string[];
  videos?: string[];
};

type SidePlaybookConfig = Record<Side, PlaybookNode[]>;
type CoachingPoints = {
  concepts: string[];
  keys: string[];
};

const OFFENSE_PLAYBOOKS: PlaybookNode[] = [
  {
    id: "master",
    label: "Master",
    children: [
      {
        id: "runs",
        label: "Runs",
        schemes: [
          {
            id: "iowa-hawkeye",
            label: "Iowa Hawkeyes",
            concept: "Inside Zone",
            drawings: [
              "/assets/drawings/deuce-rt-iowa-v-43-even-cov-4.png",
            ],
            videos: [
              "/assets/videos/Inside-Zone-01.mp4",
            ],
          },
          { 
            id: "indiana-hoosier",
            label: "Indiana Hoosiers",
            concept: "Inside Zone",
            drawings: [
              "/assets/drawings/deuce-rt-indiana-v-43-even-cov-4.png",
            ],
            videos: [
              "/assets/videos/Inside-Zone-01.mp4",
            ],
          },
          { 
            id: "wisconsin-badger",
            label: "Wisconsin Badgers",
            concept: "Fold Zone",
            drawings: [
              "/assets/drawings/deuce-rt-wisconsin-v-43-even-cov-4.png",
            ],
            videos: [
              "/assets/videos/Fold-Zone-01.mp4",
            ],
          },
          { id: "oklahoma-sooner", label: "Oklahoma Sooners", concept: "Power",
            drawings: [
              "/assets/drawings/deuce-rt-sooners-v-43-even-cov-4.png",
            ],
            videos: [
              "/assets/videos/Power-01.mp4",
            ],
          },
          { id: "purdue-boilermaker", label: "Purdue Boilermakers", concept: "Power",
            drawings: [
              "/assets/drawings/deuce-rt-purdue-v-43-even-cov-4.png",
            ],
            videos: [
              "/assets/videos/Power-01.mp4",
            ],
          },
          { id: "duke-devils", label: "Duke Devils", concept: "Duo",
            drawings: [
              "/assets/drawings/dice-rt-devils-v-43-even-cov-4.png",
            ],
            videos: [
              "/assets/videos/Duo-01.mp4",
            ],
          },
          { id: "oregon-ducks", label: "Oregon Ducks", concept: "Outside Zone",
            drawings: [
              "/assets/drawings/deuce-rt-ducks-v-43-even-cov-4.png",
            ],
            videos: [
              "/assets/videos/Outside-Zone-01.mp4",
            ],
          },
          {
            id: "carolina-tarheel",
            label: "Carolina Tarheels",
            concept: "Counter",
            drawings: [
              "/assets/drawings/deuce-rt-carolina-v-43-even-cov-4.png",
            ],
            videos: [
              "/assets/videos/Counter-01.mp4",
            ],
          },
          {
            id: "georgia-bulldog",
            label: "Georgia Bulldogs",
            concept: "GT Counter",
            drawings: [
              "/assets/drawings/deuce-rt-georgia-v-43-even-cov-4.png",
            ],
            videos: [
              "/assets/videos/GT-Counter-01.mp4",
            ],
          }
        ],
      },
      {
        id: "passes",
        label: "Passes",
        plays: ["Mesh", "Four Verticals", "Smash", "Y-Cross", "Flood"],
      },
      {
        id: "protections",
        label: "Protections",
        plays: ["Slide", "Half-Slide", "Full Man", "Scat", "Max Protect"],
      },
    ],
  },
  {
    id: "install_1",
    label: "Install 1",
    children: [
      {
        id: "runs",
        label: "Runs",
        schemes: [
          {
            id: "iowa-hawkeye",
            label: "Iowa Hawkeye",
            concept: "Inside Zone",
            drawings: [
              "/assets/drawings/play-1-drawing-01.png",
            ],
            videos: [
              "/assets/videos/Inside-Zone-01.mp4",
            ],
          },
          { id: "duke-devils", label: "Duke Devils", concept: "Counter" },
          { id: "utah-utes", label: "Utah Utes", concept: "Split Zone" },
        ],
      },
      {
        id: "passes",
        label: "Passes",
        plays: ["Hitch", "Slant/Flat", "Quick Out"],
      },
      {
        id: "protections",
        label: "Protections",
        plays: ["Slide", "Scat"],
      },
    ],
  },
  {
    id: "install_2",
    label: "Install 2",
    children: [
      {
        id: "runs",
        label: "Runs",
        schemes: [
          { id: "oregon-ducks", label: "Oregon Ducks", concept: "Outside Zone" },
          { id: "oklahoma-sooner", label: "Oklahoma Sooner", concept: "Pin & Pull" },
          { id: "duke-devils", label: "Duke Devils", concept: "Dart" },
        ],
      },
      {
        id: "passes",
        label: "Passes",
        plays: ["Snag", "Sail", "Dagger"],
      },
      {
        id: "protections",
        label: "Protections",
        plays: ["Half-Slide", "Max Protect"],
      },
    ],
  },
  {
    id: "game_1",
    label: "Game 1",
    children: [
      {
        id: "runs",
        label: "Runs",
        schemes: [
          { id: "oklahoma-sooner", label: "Oklahoma Sooner", concept: "Power" },
          { id: "duke-devils", label: "Duke Devils", concept: "Counter" },
          { id: "michigan-wolverine", label: "Michigan Wolverine", concept: "Trap" },
        ],
      },
      {
        id: "passes",
        label: "Passes",
        plays: ["Mesh", "Smash", "Y-Cross"],
      },
      {
        id: "protections",
        label: "Protections",
        plays: ["Slide", "Man", "Scat"],
      },
    ],
  },
];

const DEFENSE_PLAYBOOKS: PlaybookNode[] = [
  {
    id: "master",
    label: "Master",
    children: [
      {
        id: "fronts",
        label: "Fronts",
        plays: ["Over", "Under", "Odd", "Bear"],
      },
      {
        id: "coverages",
        label: "Coverages",
        plays: ["Cover 3", "Cover 1", "Quarters", "2-Man"],
      },
      {
        id: "pressures",
        label: "Pressures",
        plays: ["Sam Fire", "Will Cross", "Double A", "Boundary Cat"],
      },
    ],
  },
  {
    id: "install_1",
    label: "Install 1",
    children: [
      {
        id: "fronts",
        label: "Fronts",
        plays: ["Over", "Odd"],
      },
      {
        id: "coverages",
        label: "Coverages",
        plays: ["Cover 3", "Quarters"],
      },
      {
        id: "pressures",
        label: "Pressures",
        plays: ["Sam Fire", "Boundary Cat"],
      },
    ],
  },
];

const SIDE_PLAYBOOKS: SidePlaybookConfig = {
  OFFENSE: OFFENSE_PLAYBOOKS,
  DEFENSE: DEFENSE_PLAYBOOKS,
  PUNT: [
    {
      id: "master",
      label: "Master",
      children: [
        {
          id: "punt_operations",
          label: "Punt Operations",
          plays: ["Shield Punt", "Rugby Right", "Rugby Left"],
        },
      ],
    },
  ],
  PUNT_RETURN: [
    {
      id: "master",
      label: "Master",
      children: [
        {
          id: "punt_return",
          label: "Punt Return",
          plays: ["Middle Return", "Boundary Return", "Pressure Return"],
        },
      ],
    },
  ],
  KICKOFF: [
    {
      id: "master",
      label: "Master",
      children: [
        {
          id: "kickoff",
          label: "Kickoff",
          plays: ["Deep Left", "Deep Right", "Sky Kick"],
        },
      ],
    },
  ],
  KICKOFF_RETURN: [
    {
      id: "master",
      label: "Master",
      children: [
        {
          id: "kickoff_return",
          label: "Kickoff Return",
          plays: ["Middle Return", "Right Return", "Left Return"],
        },
      ],
    },
  ],
  FIELD_GOAL: [
    {
      id: "master",
      label: "Master",
      children: [
        {
          id: "field_goal",
          label: "Field Goal",
          plays: ["FG Right Hash", "FG Left Hash", "FG Middle"],
        },
      ],
    },
  ],
  FIELD_GOAL_BLOCK: [
    {
      id: "master",
      label: "Master",
      children: [
        {
          id: "field_goal_block",
          label: "Field Goal Block",
          plays: ["Edge Load", "A-Gap Surge", "Safe Block"],
        },
      ],
    },
  ],
};

const PLAYBOOK_TREE: PlaybookNode = {
  id: "root",
  label: "Playbooks",
  children: AllSideTabs.map((tab) => ({
    id: tab.value,
    label: tab.label,
    children: SIDE_PLAYBOOKS[tab.value as Side] || [],
  })),
};

const getNodeByPath = (
  root: PlaybookNode,
  path: string[]
): PlaybookNode | null => {
  let current: PlaybookNode = root;
  for (const segment of path) {
    const next: PlaybookNode | undefined = current.children?.find(
      (child: PlaybookNode) => child.id === segment
    );
    if (!next) return null;
    current = next;
  }
  return current;
};

const getNodesFromPath = (root: PlaybookNode, path: string[]): PlaybookNode[] => {
  const nodes: PlaybookNode[] = [root];
  let current = root;
  for (const segment of path) {
    const next: PlaybookNode | undefined = current.children?.find(
      (child: PlaybookNode) => child.id === segment
    );
    if (!next) break;
    nodes.push(next);
    current = next;
  }
  return nodes;
};

const isPlaysetNode = (node: PlaybookNode): boolean =>
  !node.children?.length &&
  ((Array.isArray(node.plays) && node.plays.length > 0) ||
    (Array.isArray(node.schemes) && node.schemes.length > 0));

const getSelectableItemsForNode = (node: PlaybookNode | null): string[] => {
  if (!node) return [];
  if (Array.isArray(node.schemes) && node.schemes.length > 0) {
    return node.schemes.map((scheme) => scheme.label);
  }
  return node.plays || [];
};

const getConceptGroups = (
  schemes: Scheme[]
): Array<{ concept: string; schemes: Scheme[] }> => {
  const conceptMap = new Map<string, Scheme[]>();
  schemes.forEach((scheme) => {
    const currentSchemes = conceptMap.get(scheme.concept) || [];
    currentSchemes.push(scheme);
    conceptMap.set(scheme.concept, currentSchemes);
  });
  return Array.from(conceptMap.entries()).map(([concept, conceptSchemes]) => ({
    concept,
    schemes: conceptSchemes,
  }));
};

const getSelectedSchemeForNode = (
  node: PlaybookNode | null,
  selectedItem: string | null
): Scheme | null => {
  if (!node || !selectedItem || !node.schemes?.length) return null;
  return node.schemes.find((scheme) => scheme.label === selectedItem) || null;
};

const isPathPrefix = (parentPath: string[], childPath: string[]): boolean =>
  parentPath.every((segment, index) => childPath[index] === segment);

const getDrawingUrlsForPlay = (playName: string | null): string[] => {
  if (!playName) return [];
  const normalized = playName.trim().toLowerCase();
  if (normalized === "inside zone") {
    return ["/assets/drawings/play-1-drawing-01.png"];
  }
  return [];
};

const getVideoUrlsForPlay = (playName: string | null): string[] => {
  if (!playName) return [];
  const normalized = playName.trim().toLowerCase();
  if (normalized === "inside zone") {
    return ["/assets/videos/Inside-Zone-01.mp4"];
  }
  return [];
};

const getCoachingPointsForPlay = (playName: string | null): CoachingPoints | null => {
  if (!playName) return null;
  const normalized = playName.trim().toLowerCase();
  if (normalized === "inside zone") {
    return {
      concepts: [
        "Downhill remove defenders from their spots",
        "Stay on double teams as long as possible",
        "Take 1st level defenders into the second level",
        "Ball can hit anywhere (backside cut is good)",
      ],
      keys: [
        "H Aiming point is frontside guard",
        "FST Knock out the near shoulder of defender, cannot get beat cross face",
      ],
    };
  }
  return null;
};

export const MobilePlaybooks: React.FC = () => {
  const isPortrait = useMediaQuery("(orientation: portrait)");
  const [drawerOpen, setDrawerOpen] = useState(true);
  const [drawerPath, setDrawerPath] = useState<string[]>([]);
  const [selectedPlaysetPath, setSelectedPlaysetPath] = useState<string[] | null>(
    null
  );
  const [selectedPlay, setSelectedPlay] = useState<string | null>(null);
  const [contentView, setContentView] = useState<
    "drawings" | "videos" | "coaching_points"
  >("drawings");
  const [menuAnchorEl, setMenuAnchorEl] = useState<null | HTMLElement>(null);

  const navigationPath = selectedPlaysetPath || drawerPath;
  const drawerNode = useMemo(
    () => getNodeByPath(PLAYBOOK_TREE, drawerPath) || PLAYBOOK_TREE,
    [drawerPath]
  );
  const drawerLevelNodes = drawerNode.children || [];
  const selectedPlaysetNode = useMemo(
    () =>
      selectedPlaysetPath ? getNodeByPath(PLAYBOOK_TREE, selectedPlaysetPath) : null,
    [selectedPlaysetPath]
  );
  const isViewingSelectedPlayset =
    Boolean(selectedPlaysetPath) &&
    drawerPath.join("/") === (selectedPlaysetPath || []).join("/");
  const navigationBreadcrumbs = useMemo(
    () =>
      getNodesFromPath(PLAYBOOK_TREE, navigationPath),
    [navigationPath]
  );
  const selectedScheme = useMemo(
    () => getSelectedSchemeForNode(selectedPlaysetNode, selectedPlay),
    [selectedPlaysetNode, selectedPlay]
  );
  const selectedDrawingUrls = useMemo(
    () => selectedScheme?.drawings || getDrawingUrlsForPlay(selectedPlay),
    [selectedScheme, selectedPlay]
  );
  const selectedDrawingUrl = useMemo(
    () => selectedDrawingUrls[0] || null,
    [selectedDrawingUrls]
  );
  const selectedVideoUrls = useMemo(
    () => selectedScheme?.videos || getVideoUrlsForPlay(selectedPlay),
    [selectedScheme, selectedPlay]
  );
  const selectedVideoUrl = useMemo(
    () => selectedVideoUrls[0] || null,
    [selectedVideoUrls]
  );
  const selectedCoachingPoints = useMemo(
    () => getCoachingPointsForPlay(selectedScheme?.concept || selectedPlay),
    [selectedScheme, selectedPlay]
  );
  const effectiveContentView =
    isPortrait && contentView === "coaching_points" ? "drawings" : contentView;
  const selectedPlaysetPlays = useMemo(
    () => getSelectableItemsForNode(selectedPlaysetNode),
    [selectedPlaysetNode]
  );
  const selectedPlaysetConceptGroups = useMemo(
    () =>
      selectedPlaysetNode?.schemes?.length
        ? getConceptGroups(selectedPlaysetNode.schemes)
        : [],
    [selectedPlaysetNode]
  );
  const currentPlayIndex = selectedPlay
    ? selectedPlaysetPlays.indexOf(selectedPlay)
    : -1;
  const canGoToPreviousPlay = currentPlayIndex > 0;
  const canGoToNextPlay =
    currentPlayIndex >= 0 && currentPlayIndex < selectedPlaysetPlays.length - 1;

  useEffect(() => {
    if (!selectedPlaysetPath) {
      setDrawerOpen(true);
    }
  }, [selectedPlaysetPath]);

  const toggleDrawer = () => {
    setDrawerOpen((prev) => !prev);
  };

  const handleOpenViewMenu = (event: React.MouseEvent<HTMLElement>) => {
    setMenuAnchorEl(event.currentTarget);
  };

  const handleCloseViewMenu = () => {
    setMenuAnchorEl(null);
  };

  const handleSelectView = (view: "drawings" | "videos" | "coaching_points") => {
    setContentView(view);
    setMenuAnchorEl(null);
  };

  const handlePreviousPlay = () => {
    if (!canGoToPreviousPlay) return;
    setSelectedPlay(selectedPlaysetPlays[currentPlayIndex - 1]);
  };

  const handleNextPlay = () => {
    if (!canGoToNextPlay) return;
    setSelectedPlay(selectedPlaysetPlays[currentPlayIndex + 1]);
  };

  const handleBreadcrumbClick = (index: number) => {
    const nextPath = navigationPath.slice(0, index);
    setDrawerPath(nextPath);
    if (selectedPlaysetPath && nextPath.length < selectedPlaysetPath.length) {
      setSelectedPlaysetPath(null);
      setSelectedPlay(null);
    }
    setDrawerOpen(true);
  };

  const handleDrawerNodeSelect = (node: PlaybookNode) => {
    const nextPath = [...drawerPath, node.id];
    if (isPlaysetNode(node)) {
      setSelectedPlaysetPath(nextPath);
      setSelectedPlay(null);
      setDrawerPath(nextPath);
      return;
    }
    setDrawerPath(nextPath);
    if (selectedPlaysetPath && !isPathPrefix(nextPath, selectedPlaysetPath)) {
      setSelectedPlaysetPath(null);
      setSelectedPlay(null);
    }
  };

  return (
    <Box
      sx={{
        width: "100%",
        pt: 0,
        pb: 0,
        height: "100%",
        minHeight: 0,
        position: "relative",
        display: "flex",
        flexDirection: "column",
      }}
    >
      {!isPortrait && (
        <IconButton
          onClick={() => setDrawerOpen(true)}
          aria-label="Open playset drawer"
          sx={{
            position: "absolute",
            top: 8,
            left: 8,
            zIndex: 2,
            bgcolor: "rgba(0,0,0,0.28)",
            color: "common.white",
            "&:hover": {
              bgcolor: "rgba(0,0,0,0.42)",
            },
          }}
        >
            <FolderIcon fontSize="small" />
        </IconButton>
      )}
      {!isPortrait && (
        <IconButton
          onClick={handleOpenViewMenu}
          aria-label="Open play content menu"
          sx={{
            position: "absolute",
            top: 8,
            right: 8,
            zIndex: 2,
            bgcolor: "rgba(0,0,0,0.22)",
            color: "common.white",
            "&:hover": {
              bgcolor: "rgba(0,0,0,0.36)",
            },
          }}
        >
          <MenuIcon fontSize="small" />
        </IconButton>
      )}
      <Menu
        anchorEl={menuAnchorEl}
        open={Boolean(menuAnchorEl)}
        onClose={handleCloseViewMenu}
      >
        <MenuItem onClick={() => handleSelectView("drawings")}>Drawings</MenuItem>
        {!isPortrait && (
          <MenuItem onClick={() => handleSelectView("coaching_points")}>
            Coaching Points
          </MenuItem>
        )}
        <MenuItem onClick={() => handleSelectView("videos")}>Videos</MenuItem>
      </Menu>
      {!isPortrait && (
        <Box
          sx={{
            position: "absolute",
            top: 8,
            left: 56,
            right: 56,
            zIndex: 2,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            gap: 0.5,
            pointerEvents: "none",
          }}
        >
          <IconButton
            onClick={handlePreviousPlay}
            disabled={!canGoToPreviousPlay}
            size="small"
            aria-label="Previous play"
            sx={{
              pointerEvents: "auto",
              bgcolor: "rgba(0,0,0,0.24)",
              color: "common.white",
              "&:hover": { bgcolor: "rgba(0,0,0,0.36)" },
              "&.Mui-disabled": { color: "rgba(255,255,255,0.35)" },
            }}
          >
            <ArrowBackIosNewIcon fontSize="small" />
          </IconButton>
          <Paper
            elevation={0}
            sx={{
              px: 1,
              py: 0.5,
              maxWidth: "100%",
              bgcolor: "rgba(0,0,0,0.24)",
              color: "common.white",
              borderRadius: 1,
              pointerEvents: "none",
            }}
          >
            <Typography
              variant="body2"
              sx={{ whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}
            >
              {selectedPlay || "No Play Selected"}
            </Typography>
          </Paper>
          <IconButton
            onClick={handleNextPlay}
            disabled={!canGoToNextPlay}
            size="small"
            aria-label="Next play"
            sx={{
              pointerEvents: "auto",
              bgcolor: "rgba(0,0,0,0.24)",
              color: "common.white",
              "&:hover": { bgcolor: "rgba(0,0,0,0.36)" },
              "&.Mui-disabled": { color: "rgba(255,255,255,0.35)" },
            }}
          >
            <ArrowForwardIosIcon fontSize="small" />
          </IconButton>
        </Box>
      )}

      <Paper
        elevation={2}
        sx={{
          p: 0,
          borderRadius: 0,
          flex: 1,
          minHeight: 0,
          overflow: "hidden",
          display: "flex",
          flexDirection: "column",
        }}
      >
        {isPortrait && (
          <Box
            sx={{
              px: 1,
              py: 0.75,
              borderBottom: "1px solid",
              borderColor: "divider",
              display: "flex",
              alignItems: "center",
              gap: 0.5,
            }}
          >
            <IconButton
              onClick={() => setDrawerOpen(true)}
              aria-label="Open playset drawer"
              size="small"
            >
              <FolderIcon fontSize="small" />
            </IconButton>
            <IconButton
              onClick={handlePreviousPlay}
              disabled={!canGoToPreviousPlay}
              size="small"
              aria-label="Previous play"
            >
              <ArrowBackIosNewIcon fontSize="small" />
            </IconButton>
            <Paper
              elevation={0}
              sx={{
                px: 1,
                py: 0.5,
                flex: 1,
                minWidth: 0,
                borderRadius: 1,
                bgcolor: "action.hover",
              }}
            >
              <Typography
                variant="body2"
                sx={{ whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}
              >
                {selectedPlay || "No Play Selected"}
              </Typography>
            </Paper>
            <IconButton
              onClick={handleNextPlay}
              disabled={!canGoToNextPlay}
              size="small"
              aria-label="Next play"
            >
              <ArrowForwardIosIcon fontSize="small" />
            </IconButton>
            <IconButton
              onClick={handleOpenViewMenu}
              aria-label="Open play content menu"
              size="small"
            >
              <MenuIcon fontSize="small" />
            </IconButton>
          </Box>
        )}
        <Box
          sx={{
            p: 1,
            flex: 1,
            minHeight: 0,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            textAlign: "center",
          }}
        >
          {effectiveContentView === "drawings" && isPortrait ? (
            <Box
              sx={{
                width: "100%",
                height: "100%",
                overflowY: "auto",
                p: 1,
                display: "flex",
                flexDirection: "column",
                gap: 2,
                alignItems: "stretch",
              }}
            >
              <Box
                sx={{
                  width: "100%",
                  minHeight: 220,
                  maxHeight: "52vh",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                {selectedDrawingUrl ? (
                  <Box
                    component="img"
                    src={selectedDrawingUrl}
                    alt={`${selectedPlay} drawing`}
                    sx={{
                      width: "100%",
                      height: "100%",
                      objectFit: "contain",
                      borderRadius: 1,
                    }}
                  />
                ) : (
                  <Typography variant="body2" color="text.secondary">
                    No drawing mapped for this play yet.
                  </Typography>
                )}
              </Box>

              <Box>
                <Typography variant="h6" sx={{ mb: 1.5 }}>
                  Concepts
                </Typography>
                <List disablePadding sx={{ mb: 2.5 }}>
                  {(selectedCoachingPoints?.concepts || []).map((concept) => (
                    <ListItem key={concept} disablePadding sx={{ mb: 0.75 }}>
                      <ListItemText primary={`- ${concept}`} />
                    </ListItem>
                  ))}
                  {!selectedCoachingPoints && (
                    <Typography variant="body2" color="text.secondary">
                      No coaching concepts mapped for this play yet.
                    </Typography>
                  )}
                </List>

                <Typography variant="h6" sx={{ mb: 1.5 }}>
                  Keys
                </Typography>
                <List disablePadding>
                  {(selectedCoachingPoints?.keys || []).map((keyPoint) => (
                    <ListItem key={keyPoint} disablePadding sx={{ mb: 0.75 }}>
                      <ListItemText primary={`- ${keyPoint}`} />
                    </ListItem>
                  ))}
                </List>
              </Box>
            </Box>
          ) : effectiveContentView === "drawings" && selectedDrawingUrl ? (
            <Box
              component="img"
              src={selectedDrawingUrl}
              alt={`${selectedPlay} drawing`}
              sx={{
                width: "100%",
                height: "100%",
                objectFit: "contain",
                borderRadius: 1,
              }}
            />
          ) : effectiveContentView === "videos" && selectedVideoUrl ? (
            <Box
              component="video"
              controls
              playsInline
              preload="metadata"
              sx={{
                width: "100%",
                height: "100%",
                objectFit: "contain",
                borderRadius: 1,
                bgcolor: "black",
              }}
            >
              <source src={selectedVideoUrl} type="video/mp4" />
            </Box>
          ) : effectiveContentView === "coaching_points" && selectedCoachingPoints ? (
            <Box sx={{ width: "100%", height: "100%", overflowY: "auto", p: 2 }}>
              <Typography variant="h6" sx={{ mb: 1.5 }}>
                Concepts
              </Typography>
              <List disablePadding sx={{ mb: 2.5 }}>
                {selectedCoachingPoints.concepts.map((concept) => (
                  <ListItem key={concept} disablePadding sx={{ mb: 0.75 }}>
                    <ListItemText primary={`- ${concept}`} />
                  </ListItem>
                ))}
              </List>

              <Typography variant="h6" sx={{ mb: 1.5 }}>
                Keys
              </Typography>
              <List disablePadding>
                {selectedCoachingPoints.keys.map((keyPoint) => (
                  <ListItem key={keyPoint} disablePadding sx={{ mb: 0.75 }}>
                    <ListItemText primary={`- ${keyPoint}`} />
                  </ListItem>
                ))}
              </List>
            </Box>
          ) : (
            <Box>
              <Typography variant="overline" color="text.secondary">
                {effectiveContentView.replace("_", " ")}
              </Typography>
              <Typography variant="h6" sx={{ mt: 0.5 }}>
                {selectedPlay || "Select a playset and play"}
              </Typography>
              {contentView === "drawings" && selectedPlay && !selectedDrawingUrl && (
                <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                  No drawing mapped for this play yet.
                </Typography>
              )}
              {effectiveContentView === "videos" && selectedPlay && !selectedVideoUrl && (
                <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                  No video mapped for this play yet.
                </Typography>
              )}
            </Box>
          )}
        </Box>
      </Paper>

      <Drawer
        anchor="left"
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        sx={{
          "& .MuiDrawer-paper": {
            bottom: 64,
            height: "auto",
          },
        }}
      >
        <Box
          sx={{
            width: 320,
            maxWidth: "85vw",
            height: "100%",
            display: "flex",
            flexDirection: "column",
          }}
        >
          <Box sx={{ p: 2, borderBottom: "1px solid", borderColor: "divider" }}>
            <Breadcrumbs separator="›" sx={{ overflowX: "auto" }}>
              {navigationBreadcrumbs.map((node, index) => (
                <Chip
                  key={`${node.id}-${index}`}
                  label={node.label}
                  size="small"
                  onClick={() => handleBreadcrumbClick(index)}
                  clickable
                  color={
                    index === navigationBreadcrumbs.length - 1
                      ? "primary"
                      : "default"
                  }
                  variant={
                    index === navigationBreadcrumbs.length - 1
                      ? "filled"
                      : "outlined"
                  }
                />
              ))}
            </Breadcrumbs>
          </Box>
          <Box sx={{ flex: 1, overflowY: "auto" }}>
            {!isViewingSelectedPlayset && (
              <List disablePadding>
                {drawerLevelNodes.map((node) => {
                  const nodePath = [...drawerPath, node.id];
                  const isSelectedPlayset =
                    selectedPlaysetPath?.join("/") === nodePath.join("/");
                  const isPlayset = isPlaysetNode(node);
                  return (
                    <ListItem key={node.id} disablePadding>
                      <ListItemButton
                        onClick={() => handleDrawerNodeSelect(node)}
                        selected={isSelectedPlayset}
                        sx={{ minHeight: 80 }}
                      >
                        <ListItemIcon sx={{ minWidth: 34 }}>
                          {isPlayset ? (
                            <DescriptionIcon fontSize="small" />
                          ) : (
                            <FolderIcon fontSize="small" />
                          )}
                        </ListItemIcon>
                        <ListItemText
                          primary={node.label}
                          secondary={
                            isPlayset
                              ? `${getSelectableItemsForNode(node).length} items`
                              : " "
                          }
                        />
                        <ChevronRightIcon fontSize="small" color="action" />
                      </ListItemButton>
                    </ListItem>
                  );
                })}
              </List>
            )}
            {selectedPlaysetNode && isViewingSelectedPlayset && (
              <Box sx={{ mt: 1.5 }}>
                <Box sx={{ px: 2, pb: 1 }}>
                  <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                    {selectedPlaysetNode.label}
                  </Typography>
                </Box>
                <List disablePadding>
                  {selectedPlaysetConceptGroups.length > 0 ? (
                    selectedPlaysetConceptGroups.map(({ concept, schemes }) => (
                      <Box key={concept}>
                        <ListItem disablePadding sx={{ px: 2, pt: 1.25, pb: 0.5 }}>
                          <Typography
                            variant="subtitle2"
                            color="text.secondary"
                            sx={{ fontWeight: 700 }}
                          >
                            {concept}
                          </Typography>
                        </ListItem>
                        {schemes.map((scheme) => (
                          <ListItem key={scheme.id} disablePadding>
                            <ListItemButton
                              onClick={() => {
                                setSelectedPlay(scheme.label);
                                setDrawerOpen(false);
                              }}
                              selected={scheme.label === selectedPlay}
                            >
                              <ListItemIcon sx={{ minWidth: 34 }}>
                                <DescriptionIcon fontSize="small" />
                              </ListItemIcon>
                              <ListItemText primary={scheme.label} />
                            </ListItemButton>
                          </ListItem>
                        ))}
                      </Box>
                    ))
                  ) : (
                    (selectedPlaysetNode.plays || []).map((play) => (
                      <ListItem key={play} disablePadding>
                        <ListItemButton
                          onClick={() => {
                            setSelectedPlay(play);
                            setDrawerOpen(false);
                          }}
                          selected={play === selectedPlay}
                        >
                          <ListItemIcon sx={{ minWidth: 34 }}>
                            <DescriptionIcon fontSize="small" />
                          </ListItemIcon>
                          <ListItemText primary={play} />
                        </ListItemButton>
                      </ListItem>
                    ))
                  )}
                </List>
              </Box>
            )}
            {drawerLevelNodes.length === 0 && !isViewingSelectedPlayset && (
              <Box sx={{ p: 2 }}>
                <Typography variant="body2" color="text.secondary">
                  No folders available at this level.
                </Typography>
              </Box>
            )}
          </Box>
        </Box>
      </Drawer>
    </Box>
  );
};
